import React, { useContext, useMemo, useRef } from 'react';
import { createBrowserRouter, RouterProvider, useParams, useLocation, useNavigationType } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { LoginForm, Sidebar, TenantSelectModal } from './components';
import { OfflineBanner } from './components/OfflineBanner';
import { InvalidSessionModal } from './components/InvalidSessionModal';
import { TermsModal } from './components/TermsModal';
import { RecordingToolbar } from './components/RecordingToolbar';
import {
    HomePage,
    ScreenPage,
    CreateSimpleFormPage,
    CreateFormPage,
    UpdateFormPage,
    ForgotPasswordPage,
    ResetPasswordPage,
    StatisticBuilderPage,
    AiBotsPage
} from './pages';
import { useAuth } from './hooks/useAuth';
import { useApplications } from './hooks/useApplications';
import { useTerms } from './hooks/useTerms';
import { useNewVersionReload } from './hooks/useNewVersionReload';
import { mayAcceptTerms } from './utils/roles';
import { FiltersProvider, PermissionsProvider, SidebarProvider, useFilters, useSidebar } from './contexts';
import { screenRequestFilters } from './utils/contextFilters';
import { rememberForHistoryEntry } from './utils/historyEntryMemory';
import { AppContext } from './contexts/AppContext';
import { apiService } from './services/api';
import { breadcrumbStack } from './utils/breadcrumbStack';
import { ApiErrorNotice, AppErrorBoundary, ErrorPage, RouteErrorPage } from './components/ErrorPage';

export { AppContext };

/**
 * Keeps the breadcrumb stack in sync with navigation, in ONE place:
 * - PUSH without the drill-down flag (sidebar click, programmatic nav) -> fresh trail;
 * - POP (browser back/forward AND page refresh) -> self-heal against the URL
 *   instead of blindly popping (refresh must not eat ancestors);
 * - REPLACE (query-param updates, post-save redirects) -> keep the trail.
 * Runs before paint so the Breadcrumb never renders a stale trail.
 */
function BreadcrumbNavigationSync() {
    const location = useLocation();
    const navigationType = useNavigationType();

    React.useLayoutEffect(() => {
        if (navigationType === 'PUSH') {
            if (!breadcrumbStack.consumeDrilldown()) {
                breadcrumbStack.clear();
            }
        } else if (navigationType === 'POP') {
            breadcrumbStack.consumeDrilldown();
            breadcrumbStack.heal(location.pathname);
        } else {
            breadcrumbStack.consumeDrilldown();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    return null;
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10, // 10 minutes
        },
    },
});

const router = createBrowserRouter([
    {
        path: "/",
        element: <PermissionsProvider><FiltersProvider><MainLayout><HomePage /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/screen/:screenId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><ScreenPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/statistics/:screenId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><ScreenPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/create-simple-form/:formId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><CreateSimpleFormPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/update-simple-form/:formId/:rowId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><UpdateFormPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/create-form/:formId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><CreateFormPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/app/:appId/update-form/:formId/:rowId",
        element: <PermissionsProvider><FiltersProvider><MainLayout><UpdateFormPageRoute /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/statistics/new",
        element: <PermissionsProvider><FiltersProvider><MainLayout><StatisticBuilderPage /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/statistics/:statisticId/edit",
        element: <PermissionsProvider><FiltersProvider><MainLayout><StatisticBuilderPage /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/ai-bots",
        element: <PermissionsProvider><FiltersProvider><MainLayout><AiBotsPage /></MainLayout></FiltersProvider></PermissionsProvider>
    },
    {
        path: "/forgot-password",
        element: <ForgotPasswordPage />
    },
    {
        path: "/reset-password",
        element: <ResetPasswordPage />
    },
    { path: '*', element: <ErrorPage status={404} /> }
].map(route => ({ ...route, errorElement: <RouteErrorPage /> })));

function App() {
    return (
        <AppErrorBoundary><QueryClientProvider client={queryClient}>
            <OfflineBanner />
            <RouterProvider router={router} />
            <ApiErrorNotice />
        </QueryClientProvider></AppErrorBoundary>
    );
}

function MainLayout({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading, user, handleLoginSuccess, handleLogout, showInvalidSessionModal, handleRetryLogin, setShowInvalidSessionModal, needsTenantChoice, tenantModalOpen, openTenantModal, closeTenantModal, handleTenantChosen } = useAuth();
    const { applications, selectedApplication, loadingApplications, handleApplicationSelect, handleLogoClick, loadApplications } = useApplications(isAuthenticated);
    const { terms, setTerms } = useTerms(isAuthenticated && mayAcceptTerms());
    useNewVersionReload();

    React.useEffect(() => {
        if (isAuthenticated && setShowInvalidSessionModal) {
            apiService.setInvalidSessionCallback(() => {
                setShowInvalidSessionModal(true);
            });
        }
    }, [isAuthenticated, setShowInvalidSessionModal]);

    if (loading || loadingApplications) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">Načítavam...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <LoginForm onLoginSuccess={handleLoginSuccess} />;
    }

    return (
        <AppContext.Provider value={{ applications, selectedApplication, reloadApplications: () => loadApplications({silent: true}) }}>
            <SidebarProvider>
                <BreadcrumbNavigationSync />
                <InvalidSessionModal show={showInvalidSessionModal} onLogout={handleLogout} onRetry={handleRetryLogin} />
                {user?.adminSession && (
                    <TenantSelectModal
                        show={tenantModalOpen}
                        canClose={!needsTenantChoice}
                        currentTenantName={user?.tenantName}
                        onChosen={handleTenantChosen}
                        onClose={closeTenantModal}
                        onLogout={handleLogout}
                    />
                )}
                {terms?.mustAccept && !tenantModalOpen && (
                    <TermsModal
                        terms={terms}
                        companyName={user?.tenantName}
                        onAccepted={setTerms}
                        onLogout={handleLogout}
                        onSwitchTenant={user?.adminSession ? openTenantModal : undefined}
                    />
                )}
                <PageShell
                    applications={applications}
                    selectedApplication={selectedApplication}
                    onApplicationSelect={handleApplicationSelect}
                    onLogoClick={handleLogoClick}
                    user={user}
                    onLogout={handleLogout}
                    onSwitchTenant={user?.adminSession ? openTenantModal : undefined}
                    loadingApplications={loadingApplications}
                >
                    {children}
                </PageShell>
            </SidebarProvider>
        </AppContext.Provider>
    );
}

function PageShell({ applications, selectedApplication, onApplicationSelect, onLogoClick, user, onLogout, onSwitchTenant, loadingApplications, children }: any) {
    const { isDesktopCollapsed } = useSidebar();
    return (
        <div className={`page ${isDesktopCollapsed ? 'is-side-collapsed' : ''}`}>
            <Sidebar
                applications={applications}
                selectedApplicationId={selectedApplication?.id ? String(selectedApplication.id) : undefined}
                onApplicationSelect={onApplicationSelect}
                onLogoClick={onLogoClick}
                user={user}
                onLogout={onLogout}
                onSwitchTenant={onSwitchTenant}
                loadingApplications={loadingApplications}
            />
            <div className="page-wrapper">
                {children}
            </div>
            <RecordingToolbar />
        </div>
    );
}

function findFormInScreens(screens: any[], formId: string, formType: 'createForms' | 'updateForms'): any {
    for (const screen of screens) {
        const found = screen[formType]?.find((f: any) => String(f.id) === formId);
        if (found) {
            return { ...found, screenId: screen.id };
        }
    }
    return null;
}

function ScreenPageRoute() {
    const { appId, screenId } = useParams<{ appId: string; screenId: string }>();
    const { applications } = useContext(AppContext);

    const app = applications.find((a: any) => String(a.id) === appId);
    const screen = app?.screens?.find((s: any) => String(s.id) === screenId);

    if (!app || !screen) return <ErrorPage status={404} />;

    return <ScreenPage screen={screen} appId={appId!} />;
}

function CreateSimpleFormPageRoute() {
    const { appId, formId } = useParams<{ appId: string; formId: string }>();
    const { applications } = useContext(AppContext);

    const app = applications.find((a: any) => String(a.id) === appId);
    const form = app?.screens ? findFormInScreens(app.screens, formId!, 'createForms') : null;

    if (!form) return <ErrorPage status={404} />;

    return <CreateSimpleFormPage form={form} appId={appId!} />;
}

function CreateFormPageRoute() {
    const { appId, formId } = useParams<{ appId: string; formId: string }>();
    const { applications } = useContext(AppContext);

    const app = applications.find((a: any) => String(a.id) === appId);
    const form = app?.screens ? findFormInScreens(app.screens, formId!, 'createForms') : null;

    if (!form) return <ErrorPage status={404} />;

    return <CreateFormPage form={form} appId={appId!} />;
}

function UpdateFormPageRoute() {
    const { appId, formId, rowId } = useParams<{ appId: string; formId: string; rowId: string }>();
    const { applications } = useContext(AppContext);
    const location = useLocation();
    const navigationState = (location.state as any)?.rowData;

    const app = applications.find((a: any) => String(a.id) === appId);
    const form = app?.screens ? findFormInScreens(app.screens, formId!, 'updateForms') : null;

    // Fetch fresh record from the screen owning this form. Without this,
    // rowData would always come from history state (snapshot taken when the
    // user clicked Detail) and stale on browser-back from a child screen
    // where an inline edit (e.g. price) changed a computed parent field.
    // Dynamic forms load the record shaped by the FORM (own endpoint) — the
    // screen no longer dictates which columns arrive. Classic forms keep the
    // screen-shaped read.
    const isDynamicForm = form?.kind === 'dynamic';
    const { getFilter } = useFilters();
    const formScreen = app?.screens?.find((s: any) => s.id === form?.screenId);
    const contextFilters = screenRequestFilters(formScreen, getFilter);
    // The record is a row of the form's screen, so it is asked for with that screen's list
    // parameters - the ones this page was opened with. The stored context moves on with every
    // click, so the same page reached again through the browser history would otherwise ask
    // for its record with another record's parameters and not find it.
    const recordFilters = useMemo(
        () => formScreen
            ? rememberForHistoryEntry(location.key, `record:${formScreen.id}:${rowId}`, contextFilters)
            : contextFilters,
        // The context is read once, when the history entry is first shown - not followed afterwards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.key, formScreen?.id, rowId]
    );
    // The record as read while this page is open. Once it was read, the list no longer returning it
    // is no missing page: a save can take the record out of the screen's filter (e.g. marking it
    // finished). The 404 is for a page opened on a record the list does not have.
    const recordKey = `${form?.screenId}:${rowId}:${JSON.stringify(recordFilters)}`;
    const lastRead = useRef<{ key: string; record: any } | null>(null);
    const { data: freshRowData } = useQuery({
        queryKey: isDynamicForm
            ? ['dynamicFormData', form?.id, String(rowId), 'detail']
            : ['screenData', form?.screenId, String(rowId), 'detail', JSON.stringify(recordFilters)],
        queryFn: async () => {
            if (isDynamicForm) {
                const res = await apiService.getDynamicFormRecord(form!.id, rowId!);
                if (!res.success) return null;
                const payload: any = res.data;
                return payload?.data ?? payload;
            }
            const readBefore = lastRead.current?.key === recordKey ? lastRead.current.record : null;
            const res = await apiService.getScreenRecord(form!.screenId, rowId!, recordFilters, readBefore === null);
            if (!res.success) return readBefore;
            // request() wraps the raw JSON ({success, data: <record>}) again
            // as {success, data: <raw>}, so the record is one level deeper.
            const payload: any = res.data;
            const record = payload?.data ?? payload;
            lastRead.current = { key: recordKey, record };
            return record;
        },
        enabled: !!rowId && (isDynamicForm ? !!form?.id : !!form?.screenId),
        placeholderData: navigationState,
        refetchOnMount: 'always',
    });

    if (!form) return <ErrorPage status={404} />;

    const rowData = freshRowData || navigationState || { id: rowId };

    return <UpdateFormPage form={form} rowData={rowData} appId={appId!} allScreens={app?.screens || []} />;
}

export default App;
